import { useMountedState } from 'react-use';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';

import { IKnmAttachmentDownload } from 'APIPackage/KnmModule/interfaces/IKnmAttachment';
import { IKnmDocumentSignaturesUpload } from 'APIPackage/KnmModule/interfaces/IKnmDocumentSignaturesUpload';
import {
  fetchKnmDocumentAttachmentDownload,
  fetchKnmDocumentAttachmentUpload,
  fetchKnmDocumentsByPlanIdDownload,
  fetchKnmDocumentSignaturesUpload,
  fetchPlanDocumentAttachmentUpload,
  fetchZipKnmAttachmentDownload,
  fetchKnmSignatureFileDownload,
} from 'APIPackage/KnmModule/services/knmDocumentAttachmentApi';
import { IKnmPlan } from 'PlanListPackage/MainModule/interfaces/IKnmPlan';
import { saveFile } from 'CorePackage/MainModule/common/saveFile';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { coreSetLoading } from 'CorePackage/MainModule/actions/coreActions';

export function useKnmDocumentAttachment() {
  const isMounted = useMountedState();
  const dispatch = useDispatch<Dispatch<RootActions>>();

  async function downloadKnmDocumentAttachment(attachment: IKnmAttachmentDownload) {
    try {
      dispatch(coreSetLoading(true));
      const attachmentFileData = await fetchKnmDocumentAttachmentDownload({ attachment });

      if (attachmentFileData && isMounted()) {
        saveFile(attachmentFileData, attachment.fileName || '');
      }
    } finally {
      dispatch(coreSetLoading(false));
    }
  }

  async function downloadKnmSignatureFile(filePath: string, bucket: string, isSig: boolean) {
    try {
      dispatch(coreSetLoading(true));
      const attachmentFileData = await fetchKnmSignatureFileDownload(filePath, bucket);

      if (attachmentFileData && isMounted()) {
        const filenameArray = filePath.split('/');
        const filename = filenameArray[filenameArray.length - 1];

        saveFile(attachmentFileData, isSig ? filename : `${filename}.xml`);
      }
    } finally {
      dispatch(coreSetLoading(false));
    }
  }

  async function uploadKnmDocumentAttachment(file: File) {
    try {
      dispatch(coreSetLoading(true));
      const response = await fetchKnmDocumentAttachmentUpload({
        file,
      });

      return response;
    } finally {
      dispatch(coreSetLoading(false));
    }
  }

  async function uploadPlanDocumentAttachment(file: File) {
    try {
      dispatch(coreSetLoading(true));

      return await fetchPlanDocumentAttachmentUpload({
        file,
      });
    } finally {
      dispatch(coreSetLoading(false));
    }
  }

  async function downloadZipKnmAttachment(files: IKnmAttachmentDownload[]) {
    try {
      dispatch(coreSetLoading(true));
      const attachmentZipData = await fetchZipKnmAttachmentDownload(files);

      if (attachmentZipData && isMounted()) {
        saveFile(attachmentZipData, 'Список сопроводительных документов.zip');
      }
    } finally {
      dispatch(coreSetLoading(false));
    }
  }

  async function uploadDocumentSignatures(files: IKnmDocumentSignaturesUpload) {
    try {
      dispatch(coreSetLoading(true));
      const res = await fetchKnmDocumentSignaturesUpload(files);

      return res;
    } finally {
      dispatch(coreSetLoading(false));
    }
  }

  async function getDocumentsByPlanId(planId: IKnmPlan['id']) {
    try {
      dispatch(coreSetLoading(true));
      const res = await fetchKnmDocumentsByPlanIdDownload(planId);

      return res;
    } finally {
      dispatch(coreSetLoading(false));
    }
  }

  return {
    downloadKnmDocumentAttachment,
    downloadZipKnmAttachment,
    uploadKnmDocumentAttachment,
    uploadDocumentSignatures,
    getDocumentsByPlanId,
    uploadPlanDocumentAttachment,
    downloadKnmSignatureFile,
  };
}
